import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { CATEGORY_ICONS } from '../theme/categoryIcons';
import { CategoryIcon } from './CategoryIcon';
import { Dialog, dialogStyles } from './Dialog';
import ui from './ui.module.css';
import styles from './IconPicker.module.css';

/**
 * Choose the icon a category shows in the app's Explore grid
 * (02-requirements.md §Category Icons).
 *
 * The list is **the whole Ionicons font** — every glyph the app can draw — and
 * is generated, so an administrator cannot save an icon that then falls back
 * silently on every device. The hand-labelled ones come first: the search
 * matches the pt-BR label as well as the name, because an administrator looks
 * for "padaria", not for `storefront`, and only the featured entries have a
 * label worth searching in Portuguese.
 */
/**
 * How many buttons are rendered at once. Over a thousand of them freezes the
 * dialog on open for rows nobody scrolls to; the count and the shortfall are
 * both printed, and searching reaches every icon regardless.
 */
const RENDER_LIMIT = 180;
export function IconPicker({
  categoryName,
  current,
  onPick,
  onClose,
  saving = false,
  error = null,
}: {
  categoryName: string;
  current: string | null;
  onPick: (icon: string | null) => void;
  onClose: () => void;
  saving?: boolean;
  error?: string | null;
}): ReactNode {
  const [term, setTerm] = useState('');

  const matches = useMemo(() => {
    const needle = term.trim().toLowerCase();
    if (!needle) return CATEGORY_ICONS;
    return CATEGORY_ICONS.filter(
      (icon) =>
        icon.label.toLowerCase().includes(needle) || icon.name.toLowerCase().includes(needle),
    );
  }, [term]);

  // The whole font is offered, so an unfiltered list is over a thousand
  // buttons: rendering them all costs a visible freeze every time the dialog
  // opens, for rows nobody scrolls to. The hand-labelled ones lead and the cut
  // is stated, so the answer to "where are the rest" is on screen — searching
  // reaches every one of them.
  const shown = matches.slice(0, RENDER_LIMIT);
  const hidden = matches.length - shown.length;

  return (
    <Dialog title={`Ícone de ${categoryName}`} onClose={onClose} wide>
      <p className={ui.muted}>
        O ícone aparece na grade de categorias do aplicativo. Sem ícone, o app desenha a arte
        padrão da grade.
      </p>

      <input
        className={ui.input}
        type="search"
        autoFocus
        aria-label="Buscar ícone"
        placeholder="Buscar por uso: bar, posto, futebol…"
        value={term}
        onChange={(event) => setTerm(event.target.value)}
      />

      <p className={ui.muted} aria-live="polite">
        {term.trim()
          ? `${matches.length} de ${CATEGORY_ICONS.length} ícones`
          : `${CATEGORY_ICONS.length} ícones disponíveis. Os primeiros são os mais usados — busque para chegar aos outros.`}
      </p>

      {error ? <p className={ui.error}>{error}</p> : null}

      {matches.length === 0 ? (
        <p className={ui.muted}>Nenhum ícone para “{term.trim()}”.</p>
      ) : (
        <ul className={styles.grid} aria-label="Ícones disponíveis">
          {shown.map((icon) => (
            <li key={icon.name}>
              <button
                type="button"
                disabled={saving}
                aria-pressed={icon.name === current}
                // The label is the useful half: a screen reader would otherwise
                // announce the glyph's codepoint and nothing else.
                aria-label={icon.label}
                title={icon.label}
                className={
                  icon.name === current ? `${styles.option} ${styles.chosen}` : styles.option
                }
                onClick={() => onPick(icon.name)}
              >
                <CategoryIcon name={icon.name} size="row" />
                <span className={styles.optionLabel}>{icon.label}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {hidden > 0 ? (
        <p className={ui.muted}>
          Mais {hidden} não cabem na lista. Refine a busca para chegar a eles.
        </p>
      ) : null}

      <div className={dialogStyles.actions}>
        {/* Clearing is a real choice, not a cancel: the category keeps existing
            and the app goes back to drawing the grid's own art for it. */}
        <button
          type="button"
          className={ui.ghost}
          disabled={saving || current === null}
          onClick={() => onPick(null)}
        >
          Remover ícone
        </button>
        <button type="button" className={ui.ghost} disabled={saving} onClick={onClose}>
          Fechar
        </button>
      </div>
    </Dialog>
  );
}
